import { lazy, Suspense, useEffect, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { isAdminPortal } from './lib/portal';
import { authConfigured } from './lib/api';
import { AccountDisabledPage, AdminAccessDeniedPage, AuthLoadingPage, SignInPage } from './pages/AuthPage';
import { useAuthStore } from './state/authStore';

const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const InsightsPage = lazy(() => import('./pages/InsightsPage').then((module) => ({ default: module.InsightsPage })));
const WorkbenchPage = lazy(() => import('./pages/WorkbenchPage').then((module) => ({ default: module.WorkbenchPage })));
const EntityListPage = lazy(() => import('./pages/EntityPages').then((module) => ({ default: module.EntityListPage })));
const EntityDetailPage = lazy(() => import('./pages/EntityPages').then((module) => ({ default: module.EntityDetailPage })));
const CardArenaPage = lazy(() => import('./pages/CardArenaPage').then((module) => ({ default: module.CardArenaPage })));
const CardArchetypesPage = lazy(() => import('./pages/CardArchetypesPage').then((module) => ({ default: module.CardArchetypesPage })));
const AncientListPage = lazy(() => import('./pages/AncientPages').then((module) => ({ default: module.AncientListPage })));
const AncientDetailPage = lazy(() => import('./pages/AncientPages').then((module) => ({ default: module.AncientDetailPage })));
const CoopPage = lazy(() => import('./pages/CoopPage').then((module) => ({ default: module.CoopPage })));
const CareerPage = lazy(() => import('./pages/CareerPage').then((module) => ({ default: module.CareerPage })));
const RunsPage = lazy(() => import('./pages/RunsPages').then((module) => ({ default: module.RunsPage })));
const RunDetailPage = lazy(() => import('./pages/RunsPages').then((module) => ({ default: module.RunDetailPage })));
const FacetDetailPage = lazy(() => import('./pages/DataItemPage').then((module) => ({ default: module.FacetDetailPage })));
const DataSlicePage = lazy(() => import('./pages/DataItemPage').then((module) => ({ default: module.DataSlicePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((module) => ({ default: module.SettingsPage })));
const HowToPage = lazy(() => import('./pages/HowToPage').then((module) => ({ default: module.HowToPage })));
const AdminPage = isAdminPortal ? lazy(() => import('./pages/AdminPage').then((module) => ({ default: module.AdminPage }))) : null;

function RouteLoading() {
  return <div className="route-loading" role="status">正在载入分析模块…</div>;
}

function deferred(element: ReactElement) {
  return <Suspense fallback={<RouteLoading />}>{element}</Suspense>;
}

export function App() {
  const { mode, profile, initialize } = useAuthStore();
  useEffect(() => { void initialize(); }, [initialize]);
  if (mode === 'loading') return <AuthLoadingPage />;
  if (authConfigured && mode === 'anonymous') return <SignInPage adminPortal={isAdminPortal} />;
  if (authConfigured && profile?.status === 'disabled') return <AccountDisabledPage />;
  if (isAdminPortal && profile?.role !== 'admin') return <AdminAccessDeniedPage configured={authConfigured} />;

  const analysisRoutes = <>
      <Route path="dashboard" element={deferred(<DashboardPage />)} />
      <Route path="insights" element={deferred(<InsightsPage />)} />
      <Route path="workbench" element={deferred(<WorkbenchPage />)} />
      <Route path="cards" element={deferred(<EntityListPage kind="card" />)} />
      <Route path="cards/arena" element={deferred(<CardArenaPage />)} />
      <Route path="cards/archetypes" element={deferred(<CardArchetypesPage />)} />
      <Route path="cards/:id" element={deferred(<EntityDetailPage kind="card" />)} />
      <Route path="relics" element={deferred(<EntityListPage kind="relic" />)} />
      <Route path="relics/:id" element={deferred(<EntityDetailPage kind="relic" />)} />
      <Route path="encounters" element={deferred(<EntityListPage kind="encounter" />)} />
      <Route path="encounters/:id" element={deferred(<EntityDetailPage kind="encounter" />)} />
      <Route path="ancients" element={deferred(<AncientListPage />)} />
      <Route path="ancients/:id" element={deferred(<AncientDetailPage />)} />
      <Route path="coop" element={deferred(<CoopPage />)} />
      <Route path="runs" element={deferred(<RunsPage />)} />
      <Route path="runs/:id" element={deferred(<RunDetailPage />)} />
      <Route path="characters/:id" element={deferred(<FacetDetailPage kind="character" />)} />
      <Route path="builds/:id" element={deferred(<FacetDetailPage kind="build" />)} />
      <Route path="ascensions/:id" element={deferred(<FacetDetailPage kind="ascension" />)} />
      <Route path="outcomes/:id" element={deferred(<FacetDetailPage kind="outcome" />)} />
      <Route path="parties/:id" element={deferred(<FacetDetailPage kind="party" />)} />
      <Route path="modes/:id" element={deferred(<FacetDetailPage kind="gameMode" />)} />
      <Route path="floors/:id" element={deferred(<FacetDetailPage kind="floor" />)} />
      <Route path="acts/:id" element={deferred(<FacetDetailPage kind="act" />)} />
      <Route path="dates/:id" element={deferred(<FacetDetailPage kind="date" />)} />
      <Route path="weeks/:id" element={deferred(<FacetDetailPage kind="week" />)} />
      <Route path="player-positions/:id" element={deferred(<FacetDetailPage kind="playerPosition" />)} />
      <Route path="room-types/:id" element={deferred(<FacetDetailPage kind="roomType" />)} />
      <Route path="slice" element={deferred(<DataSlicePage />)} />
      <Route path="career" element={deferred(<CareerPage />)} />
      <Route path="settings" element={deferred(<SettingsPage />)} />
      <Route path="how-to" element={deferred(<HowToPage />)} />
  </>;

  if (isAdminPortal) return <Routes>
    <Route element={<AppShell portal="admin" />}>
      <Route index element={AdminPage ? <Suspense fallback={null}><AdminPage /></Suspense> : null} />
      {analysisRoutes}
      <Route path="admin" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes>;

  return <Routes>
    <Route element={<AppShell portal="user" />}>
      <Route index element={<Navigate to="/dashboard" replace />} />
      {analysisRoutes}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Route>
  </Routes>;
}
